import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Stack,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import Hero from '../components/Hero';
import heroImage from '../assets/hero/hero-image-events.jpg';
import { getEventTypeColor, hasEvents, getEventsForDate } from '../data/events';

// Custom Day Component with event indicators
const ServerDay = (props: PickersDayProps<Dayjs> & { highlightedDays?: number[] }) => {
  const { day, ...other } = props;

  const hasEventOnDay = hasEvents(day);

  return (
    <PickersDay 
      {...other} 
      day={day} 
      selected={hasEventOnDay}
      sx={{
        '&.Mui-selected': {
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          }
        }
      }}
    />
  );
};

const Events = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  // Update to use getEventsForDate helper instead of direct filter
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const handleDateChange = (newValue: Dayjs | null) => {
    setSelectedDate(newValue);
  };

  return (
    <>
      <Hero
        title="Upcoming Events"
        description="Be part of our exciting workshops, hackathons, and social gatherings. Discover opportunities to learn, connect, and grow with the ACM ACC tech community."
        primaryButtonText="View Calendar"
        primaryButtonLink="#events-calendar"
        imageSrc={heroImage}
      />
      
      <Box 
        id="events-calendar"
        sx={{ 
          position: 'relative',
          isolation: 'isolate',
          py: { xs: 8, md: 12 },
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Calendar Section */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  height: '100%',
                  bgcolor: 'background.paper',
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateCalendar
                    value={selectedDate}
                    onChange={handleDateChange}
                    slots={{
                      day: ServerDay
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiPickersDay-root': {
                        fontSize: '1rem',
                      },
                    }}
                  />
                </LocalizationProvider>
              </Paper>
            </Grid>

            {/* Events List Section */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  height: '100%',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  {selectedDate ? (
                    `Events on ${selectedDate.format('MMMM D, YYYY')}`
                  ) : 'Select a date'}
                </Typography>

                <Stack spacing={3}>
                  {selectedDateEvents.length > 0 ? (
                    selectedDateEvents.map((event) => (
                      <Box
                        key={event.id}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                          },
                        }}
                      >
                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="h6" fontWeight={600}>
                              {event.title}
                            </Typography>
                            <Chip
                              label={event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                              sx={{
                                bgcolor: getEventTypeColor(event.type),
                                color: 'white',
                                fontWeight: 600,
                              }}
                            />
                          </Stack>
                          <Typography color="text.secondary">
                            {event.time}
                          </Typography>
                          <Typography color="text.secondary">
                            📍 {event.location}
                          </Typography>
                          <Typography>
                            {event.description}
                          </Typography>
                        </Stack>
                      </Box>
                    ))
                  ) : (
                    <Typography color="text.secondary" textAlign="center">
                      No events scheduled for this date
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Events;
